import React, { useState, useEffect, useRef, useCallback } from 'react';
import MeterDisplay from './MeterDisplay';
import FlagTripModal from './FlagTripModal';
import EditTripModal from './EditTripModal';
import {
  PASSENGER_DETERMINED_DROP,
  isMissingDropoffAddressText,
  normalizeMapLatLng,
  isValidMapPosition,
  readGoogleLatLng,
} from './locationHelper';

// Trip-edit gating (#1 Edit Trip): the driver may amend a booking only when it is
// a flag trip (arrives with no destination — "Passenger Determined") OR a cash /
// credit-card trip (no account billing). Account-billed trips are not editable
// here. The persistent client signal for billing is `waypoint.account`; there is
// no dedicated paymentType field on the waypoint (called out in the task report).
const CASH_OR_CARD_ACCOUNTS = new Set([
  '',
  'cash',
  'card',
  'credit',
  'creditcard',
  'credit card',
  'none',
  'private',
]);

function isFlagWaypoint(waypoint) {
  return (
    waypoint?.type === 'Dropoff' &&
    isMissingDropoffAddressText(waypoint?.address1) &&
    Array.isArray(waypoint?.location) &&
    Number(waypoint.location[0]) === 0 &&
    Number(waypoint.location[1]) === 0
  );
}

function isCashOrCardTrip(waypoint) {
  const acct = String(waypoint?.account || '').trim().toLowerCase();
  return CASH_OR_CARD_ACCOUNTS.has(acct);
}

const CurrentWaypoint = ({
  waypoint,
  expectedTime,
  /** { minutes, usesTraffic } — driving time from Directions when Map route runs */
  routeDriveSummary = null,
  isAccurate,
  isPhone,
  onWaypointAction,
  requestDirections,
  softMeterState,
  softMeterRouteOverride = null,
  onSetSoftMeterRouteOverride,
  onClearSoftMeterRouteOverride,
  meterRatesReady = false,
  /**
   * Flag Trip is only visible when the driver has no in-progress trip AND no meter is running.
   * Computed in LiveDispatch (currentTrip status not in {Dispatched, Enroute, ArrivedOrigin,
   * Loaded, ArrivedDestination} AND meterRunning === false), gated by fleet allowFlag setting.
   */
  canFlag = false,
  fareConfirmationOpen = false,
  center,
  onFlagTripModalClosed,
  onFlagTripModalOpen,
  onFlagTripModalDismiss,
  registerPendingFlagAck,
}) => {

const [isArrived, setIsArrived] = useState(waypoint?.arrived || false);
const [isCallout, setIsCallout] = useState(false);
const [showActionsDropdown, setShowActionsDropdown] = useState(false);
const [hasUsedMap, setHasUsedMap] = useState(false);
const [showFlagModal, setShowFlagModal] = useState(false);
const [showRouteEditor, setShowRouteEditor] = useState(false);
const [routeFormError, setRouteFormError] = useState('');
const [routeForm, setRouteForm] = useState({ destinationText: '' });
  const [placePredictions, setPlacePredictions] = useState([]);
  const [showEditTrip, setShowEditTrip] = useState(false);
  const placeIdFromSuggestionRef = useRef(null);
  const pickedDescriptionRef = useRef('');
  const debouncePredictionsRef = useRef(null);
  const showFlagButton = !!canFlag;
  const meterRunning = !!softMeterState?.isRunning;

  // #1 Edit Trip — only flag trips or cash/credit-card trips are editable.
  const flagAwaitingDestination = isFlagWaypoint(waypoint);
  const canEditTrip = flagAwaitingDestination || isCashOrCardTrip(waypoint);

  useEffect(() => {
    setIsArrived(!!waypoint?.arrived);
    setIsCallout(false);
    setShowActionsDropdown(false);
    setShowEditTrip(false);
  }, [waypoint?.jobNumber, waypoint?.type]);

  useEffect(() => {
    if (!meterRunning) {
      setShowRouteEditor(false);
      setRouteFormError('');
      setPlacePredictions([]);
      placeIdFromSuggestionRef.current = null;
      pickedDescriptionRef.current = '';
    }
  }, [meterRunning]);

  const fetchPlacePredictions = useCallback(
    (input) => {
      const trimmed = String(input || '').trim();
      if (trimmed.length < 2 || !window.google?.maps?.places?.AutocompleteService) {
        setPlacePredictions([]);
        return;
      }
      const svc = new window.google.maps.places.AutocompleteService();
      const request = { input: trimmed };
      const biased = normalizeMapLatLng(center);
      if (biased && isValidMapPosition(biased)) {
        request.location = new window.google.maps.LatLng(biased.lat, biased.lng);
        request.radius = 50000;
      }
      svc.getPlacePredictions(request, (predictions, status) => {
        const OK = window.google.maps.places.PlacesServiceStatus.OK;
        const ZERO = window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS;
        if (status !== OK && status !== ZERO) {
          setPlacePredictions([]);
          return;
        }
        setPlacePredictions(predictions || []);
      });
    },
    [center]
  );

  useEffect(() => {
    if (!showRouteEditor || !meterRunning) {
      if (debouncePredictionsRef.current) {
        clearTimeout(debouncePredictionsRef.current);
        debouncePredictionsRef.current = null;
      }
      return;
    }
    const trimmed = String(routeForm.destinationText || '').trim();
    if (trimmed.length < 2) {
      setPlacePredictions([]);
      return;
    }
    if (
      placeIdFromSuggestionRef.current &&
      trimmed === pickedDescriptionRef.current
    ) {
      return;
    }
    if (debouncePredictionsRef.current) clearTimeout(debouncePredictionsRef.current);
    debouncePredictionsRef.current = setTimeout(() => {
      debouncePredictionsRef.current = null;
      fetchPlacePredictions(trimmed);
    }, 280);
    return () => {
      if (debouncePredictionsRef.current) {
        clearTimeout(debouncePredictionsRef.current);
        debouncePredictionsRef.current = null;
      }
    };
  }, [
    showRouteEditor,
    meterRunning,
    routeForm.destinationText,
    fetchPlacePredictions,
  ]);

  const getDefaultRouteDraft = () => ({
    destinationText:
      softMeterRouteOverride?.destinationLabel ||
      String(waypoint?.address1 || '').trim(),
  });

  const openRouteEditor = () => {
    if (!meterRunning) return;
    placeIdFromSuggestionRef.current = null;
    pickedDescriptionRef.current = '';
    setPlacePredictions([]);
    const overrideOrigin = normalizeMapLatLng(softMeterRouteOverride?.origin);
    const overrideDestination = normalizeMapLatLng(softMeterRouteOverride?.destination);
    if (overrideOrigin && overrideDestination) {
      setRouteForm({
        destinationText:
          String(softMeterRouteOverride?.destinationLabel || '').trim() ||
          String(waypoint?.address1 || '').trim(),
      });
    } else {
      setRouteForm(getDefaultRouteDraft());
    }
    setRouteFormError('');
    setShowRouteEditor(true);
  };

  const handleRouteFormChange = (key, value) => {
    setRouteForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleDestinationTextChange = (value) => {
    placeIdFromSuggestionRef.current = null;
    pickedDescriptionRef.current = '';
    handleRouteFormChange('destinationText', value);
  };

  const handlePickPlacePrediction = (prediction) => {
    if (!prediction?.description || !prediction.place_id) return;
    placeIdFromSuggestionRef.current = prediction.place_id;
    pickedDescriptionRef.current = prediction.description;
    setRouteForm((prev) => ({ ...prev, destinationText: prediction.description }));
    setPlacePredictions([]);
    setRouteFormError('');
  };

  /** Geocoder.geocode is callback-only; geometry.location is google.maps.LatLng (needs readGoogleLatLng). */
  const geocodeAddressText = useCallback((address, biasCenter) => {
    return new Promise((resolve) => {
      if (!window.google?.maps?.Geocoder) {
        resolve({ results: [], status: 'NO_GEOCODER' });
        return;
      }
      const geocoder = new window.google.maps.Geocoder();
      const trimmed = String(address || '').trim();
      const biased = normalizeMapLatLng(biasCenter);
      const run = (request) => {
        geocoder.geocode(request, (results, status) => {
          resolve({
            results: status === window.google.maps.GeocoderStatus.OK ? results || [] : [],
            status,
          });
        });
      };
      if (biased && isValidMapPosition(biased)) {
        const pad = 0.6;
        run({
          address: trimmed,
          bounds: new window.google.maps.LatLngBounds(
            new window.google.maps.LatLng(biased.lat - pad, biased.lng - pad),
            new window.google.maps.LatLng(biased.lat + pad, biased.lng + pad)
          ),
        });
      } else {
        run({ address: trimmed });
      }
    });
  }, []);

  const handleRouteEditorSave = async () => {
    const origin = readGoogleLatLng(center) ?? normalizeMapLatLng(center);
    if (!origin || !isValidMapPosition(origin)) {
      setRouteFormError('Current source location is unavailable.');
      return;
    }
    const destinationText = String(routeForm.destinationText || '').trim();
    if (!destinationText) {
      setRouteFormError('Enter a destination name or address.');
      return;
    }
    const placeId = placeIdFromSuggestionRef.current;
    let destination = null;
    let destinationLabel = destinationText;

    if (placeId && window.google?.maps?.places?.PlacesService) {
      const details = await new Promise((resolve) => {
        const svc = new window.google.maps.places.PlacesService(document.createElement('div'));
        svc.getDetails(
          {
            placeId,
            fields: ['geometry', 'formatted_address', 'name'],
          },
          (place, status) => {
            const OK = window.google.maps.places.PlacesServiceStatus.OK;
            if (status !== OK || !place?.geometry?.location) {
              resolve(null);
              return;
            }
            resolve(place);
          }
        );
      });
      const loc = details?.geometry?.location;
      destination = readGoogleLatLng(loc);
      destinationLabel =
        String(details?.formatted_address || '').trim() || destinationText;
    }

    if (!destination || !isValidMapPosition(destination)) {
      if (!window.google?.maps?.Geocoder) {
        setRouteFormError('Map geocoder is not ready. Please try again.');
        return;
      }
      let { results } = await geocodeAddressText(destinationText, center);
      if (!results?.length) {
        const geocoder = new window.google.maps.Geocoder();
        results = await new Promise((resolve) => {
          geocoder.geocode({ address: destinationText }, (res, status) => {
            if (status === window.google.maps.GeocoderStatus.OK && res?.length) {
              resolve(res);
            } else {
              resolve([]);
            }
          });
        });
      }
      if (!results?.length) {
        const geocoder = new window.google.maps.Geocoder();
        results = await new Promise((resolve) => {
          geocoder.geocode(
            { address: destinationText, region: 'uk' },
            (res, status) => {
              if (status === window.google.maps.GeocoderStatus.OK && res?.length) {
                resolve(res);
              } else {
                resolve([]);
              }
            }
          );
        });
      }
      const first = results?.[0];
      const location = first?.geometry?.location;
      destination = readGoogleLatLng(location);
      destinationLabel =
        String(first?.formatted_address || '').trim() || destinationText;
    }

    if (!destination || !isValidMapPosition(destination)) {
      setRouteFormError('Could not find destination. Try a full address.');
      return;
    }
    placeIdFromSuggestionRef.current = null;
    pickedDescriptionRef.current = '';
    onSetSoftMeterRouteOverride?.({ origin, destination, destinationLabel });
    setShowRouteEditor(false);
    setRouteFormError('');
    setPlacePredictions([]);
  };

  /** Dispatch may send status casing variants */
  const isTripLoaded =
    !!waypoint &&
    String(waypoint.status ?? '')
      .trim()
      .toLowerCase() === 'loaded';

  if (!waypoint) {
    return (
      <>
        <FlagTripModal
          isOpen={showFlagModal}
          onClose={() => {
            setShowFlagModal(false);
            onFlagTripModalDismiss?.();
          }}
          currentLocation={center}
          registerPendingFlagAck={registerPendingFlagAck}
          onSuccess={(jobNumber) => {
            setShowFlagModal(false);
            if (typeof onFlagTripModalClosed === 'function') onFlagTripModalClosed(jobNumber);
          }}
        />
        <div className="w-full flex flex-col items-center justify-center py-6 px-3 bg-white border-t border-gray-200 rounded-b">
          <h2 className="text-center text-lg text-gray-800 mb-3">No Assigned Trips</h2>
          {showFlagButton && (
            <button
              type="button"
              className="text-white font-bold py-2 px-4 rounded bg-orange-500 hover:bg-orange-600 shadow"
              onClick={() => {
                onFlagTripModalOpen?.();
                setShowFlagModal(true);
              }}
            >
              Flag Trip
            </button>
          )}
        </div>
      </>
    );
  }

  const handleArrive = () => {
    if (waypoint && waypoint.jobNumber) {
      setIsArrived(true);
      if (onWaypointAction) {
        onWaypointAction('arrive', waypoint.jobNumber, waypoint.type);
      }
    }
    setShowActionsDropdown(false);
  };

  const handleLoad = () => {
    if (waypoint && waypoint.jobNumber) {
      if (onWaypointAction) {
        onWaypointAction('load', waypoint.jobNumber, waypoint.type);
      }
      setIsArrived(false);
    } else {
      console.error('Waypoint or job number is missing.');
    }
  };

  const handleComplete = () => {
    if (waypoint && waypoint.jobNumber) {
      if (onWaypointAction) {
        onWaypointAction('complete', waypoint.jobNumber, waypoint.type);
      }
    } else {
      console.error('Waypoint or job number is missing.');
    }
  };

  const handleCallout = () => {
    if (waypoint && waypoint.jobNumber) {
      setIsCallout(true);
      if (onWaypointAction) {
        onWaypointAction('callout', waypoint.jobNumber, waypoint.type);
      }
    } else {
      console.error('Waypoint or job number is missing.');
    }
    setShowActionsDropdown(false);
  }

  const handleNoShow = () => {
    if (waypoint && waypoint.jobNumber) {
      if (onWaypointAction) {
        onWaypointAction('noshow', waypoint.jobNumber, waypoint.type);
      }
    } else {
      console.error('Waypoint or job number is missing.');
    }
    setShowActionsDropdown(false);
  };

  const handleMapDirections = () => {
    if (requestDirections) {
      requestDirections();
      setHasUsedMap(true); // Track that map has been used
    }
    setShowActionsDropdown(false);
  };

        const landmarkName = waypoint.type === "Pickup"
          ? waypoint.trip?.pickup?.landmark
          : waypoint.trip?.dropoff?.landmark;

  const rawAddressPrimary = landmarkName || waypoint.address1;
  const displayAddressPrimary =
    waypoint.type === 'Dropoff' && isMissingDropoffAddressText(rawAddressPrimary)
      ? PASSENGER_DETERMINED_DROP
      : rawAddressPrimary;
  const displayAddressSecondary =
    waypoint.type === 'Dropoff' &&
    displayAddressPrimary === PASSENGER_DETERMINED_DROP &&
    waypoint.address2 &&
    !isMissingDropoffAddressText(waypoint.address2)
      ? waypoint.address2
      : waypoint.address2 !== waypoint.address1
        ? waypoint.address2
        : '';

  /** SoftMeter lives under Dropoff historically; flag / loaded pickup needs the same UI on Pickup. */
  const needsMeterSpace =
    waypoint.type === 'Dropoff' ||
    !!softMeterState?.isRunning ||
    (isTripLoaded && meterRatesReady);

  return (
    <>
    <FlagTripModal
      isOpen={showFlagModal}
      onClose={() => {
        setShowFlagModal(false);
        onFlagTripModalDismiss?.();
      }}
      currentLocation={center}
      registerPendingFlagAck={registerPendingFlagAck}
      onSuccess={(jobNumber) => {
        setShowFlagModal(false);
        if (typeof onFlagTripModalClosed === 'function') onFlagTripModalClosed(jobNumber);
      }}
    />
    <EditTripModal
      isOpen={showEditTrip}
      waypoint={waypoint}
      center={center}
      onClose={() => setShowEditTrip(false)}
      onSaved={() => setShowEditTrip(false)}
    />
    <div className='w-full flex flex-col bg-white'
      style={{
        cursor: 'pointer',
        borderRadius: '0px 0px 5px 5px',
        borderTop: '1px solid lightgray',
        minHeight: !isPhone ? '280px' : undefined,
      }}>

      <div className='flex w-full items-stretch flex-1'>
        {waypoint.type === 'Pickup' && (
          <div
            className='w-1 self-stretch flex-shrink-0'
            style={{ backgroundColor: waypoint.wheelChair ? 'rgb(66 186 240)' : 'rgb(255 210 5)' }}
          />
        )}

        {/* main card content */}
        <div className='flex flex-col w-full min-w-0 flex-1'>
          <div className='flex flex-col px-3 py-2 flex-1 min-h-0 min-w-0'>
            <div className='flex items-start gap-2'>
              <div className='flex flex-col w-2/3 min-w-0' >
                <div
                  className='font-light italic mb-1 text-xs truncate'
                  style={{ color: 'rgb(0 100 255)' }}
                >
                  {waypoint.wheelChair && 'Wheelchair'}
                </div>
                <p className='text-md font-semibold text-left overflow-hidden whitespace-nowrap text-ellipsis'>{displayAddressPrimary}</p>
                <p className='text-md font-semibold text-left overflow-hidden whitespace-nowrap text-ellipsis'>{displayAddressSecondary}</p>
              </div>
              <div className='text-xs flex flex-col w-1/3 flex-shrink-0' >
                <p className='bold'>Scheduled:</p>
                <p className='ml-2 text-right'>{waypoint.time}</p>
                {hasUsedMap && (
                  <>
                    <p className='bold'>Expected:</p>
                    <p className='ml-2 text-right'>{isAccurate ? expectedTime || waypoint.time : 'Calculating...'}</p>
                    {isAccurate && routeDriveSummary && (
                      <p className='ml-2 text-right text-gray-600' style={{ fontSize: '11px', lineHeight: 1.3 }}>
                        ~{routeDriveSummary.minutes} min
                        {routeDriveSummary.routePreference === 'shortest' ? ' · shortest' : ' · fastest'}
                        {routeDriveSummary.distanceMeters != null && routeDriveSummary.distanceMeters > 0
                          ? ` · ${(routeDriveSummary.distanceMeters / 1609.34).toFixed(1)} mi`
                          : ''}
                        {routeDriveSummary.usesTraffic ? ' · traffic' : ''}
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className='flex justify-between items-center mt-1 mb-1 min-w-0'>
              <div className='overflow-hidden whitespace-nowrap text-ellipsis min-w-0 flex-1'>
                <span className="font-bold">{waypoint.type}: </span>
                <span>{waypoint.paxName} </span>
                {waypoint.phone && <span className='text-sm ml-1'> -- {waypoint.phone}</span>}
              </div>
              {meterRunning && waypoint.type === 'Dropoff' && (
                <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                  <button
                    type="button"
                    className="text-xs text-blue-700 underline"
                    onClick={openRouteEditor}
                  >
                    Edit Source / Destination
                  </button>
                  {softMeterRouteOverride && (
                    <button
                      type="button"
                      className="text-xs text-gray-600 underline"
                      onClick={() => {
                        onClearSoftMeterRouteOverride?.();
                        setRouteFormError('');
                        setShowRouteEditor(false);
                      }}
                    >
                      Clear override
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Post-JOBMF prompt: a flag trip arrives with no destination — nudge the
                driver to add the destination & fare. Opens the same Edit Trip modal. */}
            {flagAwaitingDestination && (
              <button
                type="button"
                onClick={() => setShowEditTrip(true)}
                className="mt-1 mb-1 flex w-full items-center justify-between rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-left"
              >
                <span className="text-xs font-medium text-blue-800">
                  Destination not set — add destination &amp; fare
                </span>
                <span className="text-xs font-semibold text-blue-700 underline">Add</span>
              </button>
            )}
            {meterRunning && waypoint.type === 'Dropoff' && showRouteEditor && (
              <div className="mb-2 border border-gray-200 rounded p-2 bg-gray-50">
                <div className="flex flex-col gap-1 text-xs">
                  <span className="text-gray-600">
                    Source uses current vehicle location automatically.
                  </span>
                  <label className="flex flex-col gap-1">
                    <span>Dropoff name / address</span>
                    <div className="relative z-20">
                      <input
                        type="text"
                        autoComplete="off"
                        value={routeForm.destinationText}
                        onChange={(e) => handleDestinationTextChange(e.target.value)}
                        className="w-full border border-gray-300 rounded px-2 py-1"
                        placeholder="Start typing — suggestions appear as you type"
                      />
                      {placePredictions.length > 0 && (
                        <div className="absolute left-0 right-0 top-full z-30 mt-0.5 rounded border border-gray-200 bg-white shadow-md">
                          <ul
                            className="max-h-48 overflow-y-auto"
                            role="listbox"
                          >
                            {placePredictions.map((p) => (
                              <li key={p.place_id} className="border-b border-gray-100 last:border-b-0">
                                <button
                                  type="button"
                                  className="w-full px-2 py-2 text-left text-xs hover:bg-blue-50"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => handlePickPlacePrediction(p)}
                                >
                                  <span className="font-medium text-gray-900">
                                    {p.structured_formatting?.main_text || p.description}
                                  </span>
                                  {p.structured_formatting?.secondary_text && (
                                    <span className="block text-gray-600">
                                      {p.structured_formatting.secondary_text}
                                    </span>
                                  )}
                                </button>
                              </li>
                            ))}
                          </ul>
                          <div className="flex justify-end border-t border-gray-100 px-2 py-1">
                            <span className="text-[10px] text-gray-500">Powered by Google</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </label>
                  {routeFormError && (
                    <p className="text-red-600">{routeFormError}</p>
                  )}
                  <div className="flex gap-2 justify-end">
                    <button
                      type="button"
                      className="px-2 py-1 rounded bg-gray-200 hover:bg-gray-300"
                      onClick={() => {
                        setShowRouteEditor(false);
                        setRouteFormError('');
                        setPlacePredictions([]);
                        placeIdFromSuggestionRef.current = null;
                        pickedDescriptionRef.current = '';
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
                      onClick={handleRouteEditorSave}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            )}
            {isTripLoaded && !meterRatesReady && (
              <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mt-1">
                Meter rates for this trip were not received from dispatch. SoftMeter will stay hidden
                until the rate card arrives over the link (STARTM or JOBM).
              </p>
            )}
          </div>
          <div
            className='flex flex-col px-3 pb-2 pt-1 relative flex-shrink-0'
          >
            {waypoint.type === 'Pickup' && (
              <div className="flex flex-col w-full gap-1">
                {softMeterState?.isRunning && (
                  <MeterDisplay
                    fare={softMeterState.fare}
                    fareMode={softMeterState.fareMode}
                    distance={softMeterState.distance}
                    isRunning={softMeterState.isRunning}
                    elapsedTime={softMeterState.elapsedTime}
                    fareTimeSeconds={softMeterState.fareTimeSeconds}
                    waitTime={softMeterState.waitTime}
                    waitCharge={softMeterState.waitCharge}
                    distanceCharge={softMeterState.distanceCharge}
                    timeCharge={softMeterState.timeCharge}
                    flagRate={softMeterState.flagRate}
                  />
                )}
              <div className='flex space-x-2 w-full'>
                {/* Actions Dropdown */}
                <div className='relative flex-1'>
                  <button 
                    className='text-white font-bold py-1 px-2 rounded w-full bg-orange-500 hover:bg-orange-600'
                    onClick={() => setShowActionsDropdown(!showActionsDropdown)}
                  >
                    Actions ▼
                  </button>
                  {showActionsDropdown && (
                    <div className='absolute bottom-full left-0 mb-1 bg-white border border-gray-300 rounded shadow-lg z-10 w-full'>
                      <button 
                        className='block w-full text-left px-3 hover:bg-green-100 text-green-600'
                        onClick={handleMapDirections}
                      >
                        Map
                      </button>
                      <button 
                        className='block w-full text-left px-3  hover:bg-orange-100 text-orange-600'
                        onClick={handleCallout}
                        disabled={isCallout}
                      >
                        {isCallout ? '✓ Called Out' : 'Callout'}
                      </button>
                      <button 
                        className='block w-full text-left px-3 hover:bg-blue-100 text-blue-600'
                        onClick={handleArrive}
                        disabled={isArrived}
                      >
                        {isArrived ? '✓ Arrived' : 'Arrive'}
                      </button>
                      <button 
                        className='block w-full text-left px-3 hover:bg-red-100 text-red-600'
                        onClick={handleNoShow}
                      >
                        No Show
                      </button>
                      {showFlagButton && (
                        <button
                          type="button"
                          className="block w-full text-left px-3 hover:bg-yellow-100 text-yellow-700"
                          onClick={() => {
                            onFlagTripModalOpen?.();
                            setShowFlagModal(true);
                            setShowActionsDropdown(false);
                          }}
                        >
                          Flag trip
                        </button>
                      )}
                      {canEditTrip && (
                        <button
                          type="button"
                          className="block w-full text-left px-3 hover:bg-blue-100 text-blue-700"
                          onClick={() => {
                            setShowEditTrip(true);
                            setShowActionsDropdown(false);
                          }}
                        >
                          Edit Trip
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Load Button */}
                <button 
                  className='text-white font-bold py-1 px-2 rounded flex-1'
                  style={{ backgroundColor: '#0072B2', opacity: isTripLoaded ? 0.55 : 1 }}
                  onClick={handleLoad}
                  disabled={isTripLoaded}
                  title={isTripLoaded ? 'Trip already loaded — waiting for next stop' : undefined}
                >
                  Pickup
                </button>
              </div>
              </div>
            )}
            {waypoint.type === 'Dropoff' &&
              <div className='flex flex-col w-full gap-1'>
                {/* SoftMeter — live fare while running */}
                {softMeterState?.isRunning && (
                  <MeterDisplay
                    fare={softMeterState.fare}
                    fareMode={softMeterState.fareMode}
                    distance={softMeterState.distance}
                    isRunning={softMeterState.isRunning}
                    elapsedTime={softMeterState.elapsedTime}
                    fareTimeSeconds={softMeterState.fareTimeSeconds}
                    waitTime={softMeterState.waitTime}
                    waitCharge={softMeterState.waitCharge}
                    distanceCharge={softMeterState.distanceCharge}
                    timeCharge={softMeterState.timeCharge}
                    flagRate={softMeterState.flagRate}
                  />
                )}
                <div className='flex space-x-2 w-full'>
                  {/* Actions Dropdown */}
                  <div className='relative flex-1'>
                    <button 
                      className='text-white font-bold py-1 px-2 rounded w-full bg-orange-500 hover:bg-orange-600'
                      onClick={() => setShowActionsDropdown(!showActionsDropdown)}
                    >
                      Actions ▼
                    </button>
                    {showActionsDropdown && (
                      <div className='absolute bottom-full left-0 mb-1 bg-white border border-gray-300 rounded shadow-lg z-10 w-full'>
                        <button 
                          className='block w-full text-left px-3  hover:bg-green-100 text-green-600'
                          onClick={handleMapDirections}
                        >
                          Map
                        </button>
                        <button 
                          className='block w-full text-left px-3 hover:bg-blue-100 text-blue-600'
                          onClick={handleArrive}
                          disabled={isArrived}
                        >
                          {isArrived ? '✓ Arrived' : 'Arrive'}
                        </button>
                        {showFlagButton && (
                          <button
                            type="button"
                            className="block w-full text-left px-3 hover:bg-yellow-100 text-yellow-700"
                            onClick={() => {
                              onFlagTripModalOpen?.();
                              setShowFlagModal(true);
                              setShowActionsDropdown(false);
                            }}
                          >
                            Flag Trip
                          </button>
                        )}
                        {canEditTrip && (
                          <button
                            type="button"
                            className="block w-full text-left px-3 hover:bg-blue-100 text-blue-700"
                            onClick={() => {
                              setShowEditTrip(true);
                              setShowActionsDropdown(false);
                            }}
                          >
                            Edit Trip
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    className='text-white font-bold py-1 px-2 rounded flex-1'
                    style={{ backgroundColor: '#0072B2' }}
                    onClick={handleComplete}
                  >
                    Complete
                  </button>
                </div>
              </div>
            }
          </div>
        </div>

        {waypoint.type === 'Dropoff' && (
          <div
            className='w-1 self-stretch flex-shrink-0'
            style={{ backgroundColor: waypoint.wheelChair ? 'rgb(66 186 240)' : 'rgb(255 210 5)' }}
          />
        )}

      </div>
    </div>
    </>
  );
};

export default CurrentWaypoint;
